/**
 * ProPlusClaimModal — iter87.199.7
 *
 * Fires the first time a Pro+ user opens a rivalry where an admin
 * added them as a placeholder Participant (`is_placeholder=true`).
 * Backend's `openLeagueSession` returns `{needs_username_pick: true,
 * claim_prompt: {...}}` and this modal lets them pick their username
 * before we materialize the session + navigate.
 *
 * Visual pattern mirrors the magic-link /join page (Crown icon,
 * "Join <league>" heading, invited-by, email-on-file pill, username
 * input, invite/personal codes callout at the bottom) — MINUS the
 * 18+ / T&C consent modal, since Pro+ signup already gated on those
 * (see the `claim_placeholder` backend handler for the compliance
 * rationale).
 */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Crown, X } from "lucide-react";
import { toast } from "sonner";
import { proApi } from "@/pro/api";
import { session as legacySession } from "@/lib/api";

export default function ProPlusClaimModal({ open, prompt, leagueId, leagueName, inviteCode, onClose, onClaimed }) {
    const [username, setUsername] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open && prompt) {
            setUsername(prompt.suggested_username || "");
            setError(null);
            // Focus the username input after mount.
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open, prompt]);

    if (!open || !prompt) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const picked = username.trim();
        if (!picked) {
            setError("Pick a username to continue.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const s = await proApi.claimPlaceholder(leagueId, picked);
            legacySession.save({
                league: { id: s.league_id, name: leagueName || prompt.league_name, invite_code: inviteCode || prompt.invite_code || null },
                participant: {
                    id: s.participant_id,
                    username: s.username,
                    email: null,
                    is_admin: s.is_admin,
                },
            });
            toast.success(`Welcome to ${prompt.league_name || "the rivalry"}, ${s.username}!`);
            onClaimed?.(s);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Unable to claim your spot — try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ backgroundColor: "#000000" /* SOLID - no transparency - do not modify */ }}
            data-testid="pro-plus-claim-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <form
                onSubmit={submit}
                data-testid="pro-plus-claim-modal"
                className="max-w-md w-full p-6 space-y-5 border border-white/[0.08] rounded-2xl"
                style={{ backgroundColor: "#111111" /* SOLID - no transparency - do not modify */ }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 bg-cyber border-2 border-black shadow-brutalYellow flex items-center justify-center shrink-0">
                            <Crown className="w-5 h-5 text-black" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Tiny Crew Rivalries</p>
                            <h1 className="font-display font-black text-xl text-white leading-tight truncate">
                                Join <span className="text-cyber">{prompt.league_name || leagueName}</span>
                            </h1>
                            <p className="text-xs text-zinc-400 mt-1">
                                Invited by <strong className="text-white">{prompt.admin_username}</strong>
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="pro-plus-claim-close-btn"
                        className="brutal-btn-ghost p-1.5 shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {prompt.email && (
                    <div className="text-xs">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Email on file</p>
                        <div data-testid="pro-plus-claim-email-pill" className="pill bg-ink border-[#333] text-zinc-300 text-[11px]">
                            {prompt.email.toUpperCase()}
                        </div>
                    </div>
                )}

                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Pick a username</span>
                    <input
                        ref={inputRef}
                        data-testid="pro-plus-claim-username-input"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="e.g. SkyHawk22"
                        maxLength={40}
                        className="brutal-input w-full mt-1"
                    />
                    <span className="text-[10px] text-zinc-600 mt-1 block">
                        Case doesn't matter for signing in — TKA and tka are treated the same.
                    </span>
                </label>

                {error && (
                    <p data-testid="pro-plus-claim-error" className="text-xs text-heat">{error}</p>
                )}

                <button
                    data-testid="pro-plus-claim-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="brutal-btn bg-cyber text-black w-full font-display font-black text-sm"
                >
                    {submitting ? "Joining..." : "Continue"}
                </button>

                {(prompt.invite_code || prompt.personal_code) && (
                    <div className="pt-3 border-t border-[#222] space-y-1.5">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Save these for future logins</p>
                        {prompt.invite_code && (
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-zinc-500">Rivalry code</span>
                                <span data-testid="pro-plus-claim-invite-code" className="font-mono font-bold text-cyber">{prompt.invite_code}</span>
                            </div>
                        )}
                        {prompt.personal_code && (
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-zinc-500">Personal code</span>
                                <span data-testid="pro-plus-claim-personal-code" className="font-mono font-bold text-volt">{prompt.personal_code}</span>
                            </div>
                        )}
                        <p className="text-[10px] text-zinc-600 pt-1">
                            These are your <strong className="text-zinc-400">permanent</strong> sign-in credentials for this rivalry.
                        </p>
                    </div>
                )}
            </form>
        </div>,
        document.body
    );
}
